<template>
    <div class="card mt-2">
        <div class="card-header d-flex justify-content-between align-items-start">
            <h5 class="card-title">Recorded Segments</h5>
            <b-button-group v-if="(recordedSegments.length > 0)&& (recordedSegments[0].adjournmentType != prePostRecordingAdjournmentType)">
                <b-dropdown size="sm" variant="outline-secondary" right v-if="!readonly">
                    <template v-slot:button-content>
                        Actions
                    </template>
                    <b-dropdown-item v-on:click="addSegmentZeroDialogOpen">
                        <div class="text-secondary"><i class="fas fa-plus mr-1"></i>Add Segment 0</div>
                    </b-dropdown-item>
                </b-dropdown>
            </b-button-group>
        </div>
        <ul class="list-group list-group-flush" v-if="recordedSegments.length == 0">
            <li class="list-group-item">
                The list is currently empty
            </li>
        </ul>
        <b-table striped hover :items="recordedSegments" :fields="tableFields" v-if="(recordedSegments.length > 0)">
            <template v-slot:cell(actions)="row" v-if="!readonly">
                <b-button-group>
                    <b-button size="sm"
                              key="edit"
                              variant="outline-secondary"
                              v-on:click="editRecordedSegment(row.item)"
                              v-if="editRecordedSegmentActionAvailable(row.item)">
                        <i class="fas fa-edit mr-1"></i>Edit
                    </b-button>
                    <b-button size="sm"
                              key="remove"
                              variant="outline-danger"
                              v-on:click="removeRecordedSegment(row.item)"
                              v-if="removeRecordedSegmentActionAvailable(row.item)">
                        <i class="fas fa-times mr-1"></i>Remove
                    </b-button>
                </b-button-group>
            </template>
        </b-table>
        <ul class="list-group list-group-flush" v-if="canAddMoreSegments">
            <li class="list-group-item">
                <validation-observer ref="nextRecordedSegmentValidator" v-slot="{ passes }" slim>
                    <b-alert v-model="showNextRecordedSegmentErrors" variant="danger" dismissible>
                        <div v-for="(error, index) in nextRecordedSegmentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <b-form-row>
                        <validation-provider rules="required" name="Start time" v-slot="{ errors }" slim>
                            <b-form-group label="Start Time"
                                          label-for="startTime"
                                          class="col-md-2">
                                <b-form-input ref="startTime"
                                              name="startTime"
                                              type="time"
                                              step="1"
                                              v-model="nextRecordedSegment.startTime"
                                              :state="errors[0] ? false : null"
                                              :readonly="recordedSegments.length == 0">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider rules="required" name="End time" v-slot="{ errors }" slim>
                            <b-form-group label="End Time"
                                          label-for="endTime"
                                          class="col-md-2">
                                <b-form-input ref="endTime"
                                              name="endTime"
                                              type="time"
                                              step="1"
                                              v-model="nextRecordedSegment.endTime"
                                              :state="errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider rules="required" name="Adjournment Type" v-slot="{ valid, errors }" slim>
                            <b-form-group label="Adjournment Type"
                                          label-for="adjournmentType"
                                          class="col-md-6">
                                <select2 ref="adjournmentTypeSelectForNextRecordedSegment"
                                         name="adjournmentType"
                                         placeholder="Select an adjournment type"
                                         v-model="nextRecordedSegment.adjournmentType"
                                         :options="adjournmentTypesForNextRecordedSegment"
                                         disable-search
                                         :state="errors[0] ? false : null">
                                </select2>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider rules="required" name="Presiding Officers" v-slot="{ valid, errors }" slim v-if="listCourt">
                            <b-form-group label="Presiding Officers"
                                          label-for="judges"
                                          class="col-md-10">
                                <select2 name="judges"
                                         placeholder="Select presiding officers"
                                         v-model="nextRecordedSegment.assignedCourtJudgeIds"
                                         :options="allJudges"
                                         multiple
                                         :state="errors[0] ? false : null">
                                </select2>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <b-form-group label="Action"
                                      class="col-md-2 pl-3">
                            <b-button variant="outline-secondary" v-on:click.prevent="passes(saveNextRecordedSegment)"><i class="fas fa-plus mr-1"></i>Add Segment</b-button>
                        </b-form-group>
                    </b-form-row>
                </validation-observer>
            </li>
        </ul>
        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered
                     title="Segment Details"
                     v-on:shown="onModalShown"
                     v-on:hidden="onModalHidden"
                     v-model="editRecordedSegmentDialogOpen"
                     v-on:ok.prevent="passes(updateRecordedSegment)">
                <b-alert v-model="showEditRecordedSegmentErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in editRecordedSegmentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <validation-provider rules="required" name="Start Time" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Start Time"
                                      label-for="startTime"
                                      class="col-md-3">
                            <b-form-input name="startTime"
                                          type="time"
                                          step="1"
                                          v-model="currentRecordedSegment.startTime"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required" name="End Time" v-slot="{ valid, errors }" slim>
                        <b-form-group label="End Time"
                                      label-for="endTime"
                                      class="col-md-3">
                            <b-form-input name="endTime"
                                          type="time"
                                          step="1"
                                          v-model="currentRecordedSegment.endTime"
                                          :disabled="modifyOnly && currentRecordedSegment.lastSegment"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required" name="Adjournment Type" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Adjournment Type"
                                      label-for="adjournmentType"
                                      class="col-md-6">
                            <select2 name="adjournmentType"
                                     placeholder="Select an adjournment type"
                                     v-model="currentRecordedSegment.adjournmentType"
                                     :options="adjournmentTypesForEditRecordedSegment"
                                     disable-search
                                     :disabled="modifyOnly && currentRecordedSegment.lastSegment"
                                     :state="errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>



                <b-form-row v-if="listCourt">
                    <validation-provider rules="required" name="Presiding Officers" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Presiding Officers"
                                      label-for="presidingOfficers"
                                      class="col-md-12">
                            <select2 name="presidingOfficers"
                                     placeholder="Select presiding officers"
                                     v-model="currentRecordedSegment.assignedCourtJudgeIds"
                                     :options="allJudges"
                                     multiple
                                     :state="errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered
                     title="Segment 0 Details"
                     v-on:shown="onModalShown"
                     v-on:hidden="onModalHidden"
                     v-model="addSegment0DialogOpen"
                     v-on:ok.prevent="passes(addSegment0)">
                <b-alert v-model="showAddSegment0Errors" variant="danger" dismissible>
                    <div v-for="(error, index) in addSegment0Errors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <validation-provider rules="required" name="Start Time" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Start Time"
                                      label-for="startTime"
                                      class="col-md-3">
                            <b-form-input name="startTime"
                                          type="time"
                                          step="1"
                                          v-model="segment0.startTime"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required" name="Adjournment Type" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Adjournment Type"
                                      label-for="adjournmentType"
                                      class="col-md-6">
                            <select2 name="adjournmentType"
                                     placeholder="Select an adjournment type"
                                     v-model="segment0.adjournmentType"
                                     :options="adjournmentTypesForSegment0"
                                     disable-search
                                     :disabled="modifyOnly && currentRecordedSegment.lastSegment"
                                     :state="errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>

    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);
    
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import ErrorsMixin from '../mixins/ErrorsMixin';
    import Select2 from './Select2';

    Vue.use(BootstrapVue);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('select2', Select2);

    export default {
        name: 'RecordedSegments',
        mixins: [DateTimeMixin, ErrorsMixin],
        props: {
            apiControllerUrl: {
                type: String,
                required: true
            },
            workItemId: {
                type: String,
                required: true
            },
            workItemStartTime: {
                type: Date,
                required: true
            },
            initialAssignedCourtJudgeIds: {
                type: Array,
                default: null
            },
            workItemCompleted: {
                type: Boolean,
                default: false
            },
            allAdjournmentTypes: {
                type: Array,
                required: true
            },
            allJudges: {
                type: Array,
                default: null
            },
            finalAdjournmentType: {
                type: Number,
                required: true
            },
            prePostRecordingAdjournmentType: {
                type: Number,
                required: true
            },
            listCourt: {
                type: Boolean,
                default: false
            },
            modifyOnly: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: false
            }
        },
        data: function () {
            return {
                adjournmentTypesForNextRecordedSegment: [],
                previousExcludePrePostRecording: false,
                previousPrePostRecordingOnly: false,
                adjournmentTypesForEditRecordedSegment: [],
                adjournmentTypesForSegment0: [],

                lastSegmentEndTime: null,
                lastSegmentAdjournmentType: null,
                lastSegmentAssignedCourtJudgeIds: null,
                recordedSegments: [],
                nextRecordedSegment: {
                    segmentId: null,
                    startTime: null,
                    endTime: null,
                    adjournmentType: null,
                    assignedCourtJudgeIds: null,
                },
                nextRecordedSegmentErrors: [],
                currentRecordedSegment: {
                    segmentId: null,
                    startTime: null,
                    endTime: null,
                    adjournmentType: null,
                    assignedCourtJudgeIds: null,
                    firstSegment: false
                },
                segment0: {
                    segmentId: null,
                    startTime: null,
                    endTime: null,
                    adjournmentType: null,
                    assignedCourtJudgeIds: null,
                    firstSegment: true
                },
                editRecordedSegmentDialogOpen: false,
                editRecordedSegmentErrors: [],
                addSegment0DialogOpen: false,
                addSegment0Errors: [],
                currentErrors: [],
            };
        },
        watch: {
            readonly: function () {
                this.reload(true);
            },
        },
        computed: {
            tableFields: function() {
                var fields = [
                    {
                        key: 'startTime',
                        label: 'Start Time',
                        formatter: value => {
                            return this.formatDateTime(value, 'hh:mm:ssa', false);
                        },
                        class: 'fit'
                    },
                    {
                        key: 'endTime',
                        label: 'End Time',
                        formatter: value => {
                            return this.formatDateTime(value, 'hh:mm:ssa', false);
                        },
                        class: 'fit'
                    },
                    {
                        key: 'adjournmentTypeDisplayName',
                        label: 'Adjournment Type'
                    }
                ];
                if (this.listCourt) {
                    fields.push(
                        {
                            key: 'assignedCourtJudgesDisplayText',
                            label: 'Presiding Officers'
                        }
                    );
                }
                fields.push(
                    {
                        key: 'actions',
                        label: 'Actions',
                        class: 'fit'
                    }
                );
                return fields;
            },
            showNextRecordedSegmentErrors: function () {
                return this.nextRecordedSegmentErrors.length > 0;
            },
            showEditRecordedSegmentErrors: function () {
                return this.editRecordedSegmentErrors.length > 0;
            },
            showAddSegment0Errors: function () {
                return this.addSegment0Errors.length > 0;
            },
            canAddMoreSegments: function () {
                if (this.readonly) return false;
                if (this.modifyOnly) return false;
                if ((this.recordedSegments == null) || (this.recordedSegments.length == 0)) return true;

                var lastSegment = this.recordedSegments[this.recordedSegments.length - 1];

                // original
                //return lastSegment.adjournmentType != this.finalAdjournmentType;

                // josef
                return ((this.recordedSegments.length == 1) || (lastSegment.adjournmentType != this.prePostRecordingAdjournmentType));
            }
        },
        methods: {
            reload: function (resetInputFields = false) {
                this.currentErrors = [];
                var previousSegmentCount = this.recordedSegments ? this.recordedSegments.length : 0;
                var previousLastSegmentEndTime = this.lastSegmentEndTime;
                var previousLastSegmentAdjournmentType = this.lastSegmentAdjournmentType;
                return axios
                    .get(this.apiControllerUrl + '/' + this.workItemId)
                    .then(response => {
                        this.recordedSegments = $.map(response.data, obj => {
                            obj.startTime = this.parseDateTime(obj.startTime, null, false);
                            obj.endTime = this.parseDateTime(obj.endTime, null, false);
                            this.lastSegmentEndTime = obj.endTime;
                            this.lastSegmentAdjournmentType = obj.adjournmentType;
                            this.lastSegmentAssignedCourtJudgeIds = obj.assignedCourtJudgeIds;
                            return obj;
                        });
                        if (resetInputFields && this.canAddMoreSegments) {
                            var nextSegmentStartTime = this.lastSegmentEndTime;
                            if (nextSegmentStartTime == null) nextSegmentStartTime = this.workItemStartTime;
                            var nextSegmentAssignedCourtJudgeIds = this.lastSegmentAssignedCourtJudgeIds;
                            if (nextSegmentAssignedCourtJudgeIds == null) nextSegmentAssignedCourtJudgeIds = this.initialAssignedCourtJudgeIds;
                            this.nextRecordedSegment = {
                                segmentId: null,
                                startTime: this.formatDateTime(nextSegmentStartTime, 'HH:mm:ss', false),
                                endTime: null,
                                adjournmentType: null,
                                assignedCourtJudgeIds: nextSegmentAssignedCourtJudgeIds
                            };
                            if (this.$refs.nextRecordedSegmentValidator) this.$refs.nextRecordedSegmentValidator.reset();
                            if (this.recordedSegments.length == 0) {
                                if (this.$refs.endTime) this.$refs.endTime.focus();
                            }
                            else {
                                if (this.$refs.startTime) this.$refs.startTime.focus();
                            }
                        }

                        var excludePrePostRecording = false;
                        var prePostRecordingOnly = false;
                        if (this.recordedSegments.length > 0) {
                            excludePrePostRecording = true;
                        }
                        if (this.lastSegmentAdjournmentType == this.finalAdjournmentType) {
                            excludePrePostRecording = false;
                            prePostRecordingOnly = true;
                        }
                        if ((excludePrePostRecording != this.previousExcludePrePostRecording) || (prePostRecordingOnly != this.previousPrePostRecordingOnly)) {
                            this.previousExcludePrePostRecording = excludePrePostRecording;
                            this.previousPrePostRecordingOnly = prePostRecordingOnly;
                            this.adjournmentTypesForNextRecordedSegment = this.allAdjournmentTypes.filter(adjournmentType => {
                                if (excludePrePostRecording) {
                                    if (adjournmentType.value != "PrePostRecording") return true;
                                } else if (prePostRecordingOnly) {
                                    if (adjournmentType.value == "PrePostRecording") return true;
                                }
                                return false;
                            });
                        }
                        this.adjournmentTypesForSegment0 = this.allAdjournmentTypes.filter(adjournmentType => {
                            if (adjournmentType.value == "PrePostRecording") return true;
                            return false;
                        });
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        var segmentCount = this.recordedSegments ? this.recordedSegments.length : 0;
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        } else if ((segmentCount != previousSegmentCount) || (this.lastSegmentEndTime != previousLastSegmentEndTime) || (this.lastSegmentAdjournmentType != previousLastSegmentAdjournmentType)) {
                            this.$emit('change', segmentCount, this.lastSegmentEndTime, this.lastSegmentAdjournmentType)
                        }
                    });
            },
            saveNextRecordedSegment: function () {
                var data = {
                    workItemId: this.workItemId,
                    startTime: this.parseDateTime(this.nextRecordedSegment.startTime, 'HH:mm:ss'),
                    endTime: this.parseDateTime(this.nextRecordedSegment.endTime, 'HH:mm:ss'),
                    adjournmentType: this.nextRecordedSegment.adjournmentType,
                    assignedCourtJudgeIds: this.nextRecordedSegment.assignedCourtJudgeIds
                };
                this.nextRecordedSegmentErrors = [];
                axios
                    .post(this.apiControllerUrl, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload(true);
                    })
                    .catch(ex => {
                        this.nextRecordedSegmentErrors = this.handleAxiosException(ex);
                    });
            },
            addSegmentZeroDialogOpen: function () {
                this.addSegment0DialogOpen = true;
            },
            addSegment0: function () {
                var data = {
                    workItemId: this.workItemId,
                    startTime: this.parseDateTime(this.segment0.startTime, 'HH:mm:ss'),
                    assignedCourtJudgeIds: this.segment0.assignedCourtJudgeIds,
                    adjournmentType: this.segment0.adjournmentType,
                    segment0: true
                };
                this.addSegment0Errors = [];


                axios
                    .post(this.apiControllerUrl, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload(true);
                        this.addSegment0DialogOpen = false;
                    })
                    .catch(ex => {
                        this.addSegment0Errors = this.handleAxiosException(ex);
                    });
            },
            editRecordedSegmentActionAvailable: function () {
                return !this.readonly;
            },
            editRecordedSegment: function (recordedSegment) {
                var position = this.recordedSegments.indexOf(recordedSegment);
                this.currentRecordedSegment = {
                    segmentId: recordedSegment.segmentId,
                    startTime: this.formatDateTime(recordedSegment.startTime, 'HH:mm:ss', false),
                    endTime: this.formatDateTime(recordedSegment.endTime, 'HH:mm:ss', false),
                    adjournmentType: recordedSegment.adjournmentTypeString,
                    assignedCourtJudgeIds: recordedSegment.assignedCourtJudgeIds,
                    firstSegment: (position == 0),
                    lastSegment: (recordedSegment.adjournmentType == this.finalAdjournmentType)
                };
                this.editRecordedSegmentErrors = [];
                this.editRecordedSegmentDialogOpen = true;
            },
            updateRecordedSegment: function () {
                var data = {
                    workItemId: this.workItemId,
                    segmentId: this.currentRecordedSegment.segmentId,
                    startTime: this.parseDateTime(this.currentRecordedSegment.startTime, 'HH:mm:ss'),
                    endTime: this.parseDateTime(this.currentRecordedSegment.endTime, 'HH:mm:ss'),
                    adjournmentType: this.currentRecordedSegment.adjournmentType,
                    assignedCourtJudgeIds: this.currentRecordedSegment.assignedCourtJudgeIds
                };
                this.editRecordedSegmentErrors = [];
                axios
                    .request(
                        {
                            method: (data.segmentId == null) ? 'post' : 'put',
                            url: this.apiControllerUrl + ((data.segmentId == null) ? '' : '/' + data.segmentId),
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload(true);
                    })
                    .catch(ex => {
                        this.editRecordedSegmentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.editRecordedSegmentErrors.length == 0) {
                            this.editRecordedSegmentDialogOpen = false;
                        }
                    })
            },
            removeRecordedSegmentActionAvailable: function (recordedSegment) {
                if (this.readonly) return false;
                var position = this.recordedSegments.indexOf(recordedSegment);
                if (position == 0 && (recordedSegment.adjournmentType != this.prePostRecordingAdjournmentType)) return false;
                if (!this.modifyOnly) return true;
                return recordedSegment.adjournmentType != this.finalAdjournmentType;
            },
            removeRecordedSegment: function (recordedSegment) {
                this.currentErrors = [];
                axios
                    .delete(this.apiControllerUrl + '/' + recordedSegment.segmentId)
                    .then(() => {
                        this.reload(true);
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length > 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                    });
            },
            onModalHidden() {
                this.$emit('modalOpen', false);
            },
            onModalShown() {
                this.$emit('modalOpen', true);
            },
        },
        mounted: function () {
            this.adjournmentTypesForNextRecordedSegment = JSON.parse(JSON.stringify(this.allAdjournmentTypes));
            this.adjournmentTypesForEditRecordedSegment = JSON.parse(JSON.stringify(this.allAdjournmentTypes));
            this.reload(true);
        }
    }
</script>